import React, { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Sparkles, Loader2, CheckCircle2, AlertCircle, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Avatar from '@/components/common/Avatar';
import { bajaApi } from '../../../hooks/api/baja';
import type {
  BajaPaciente,
  HcValidationResult,
  HistoriaClinica,
  HistoriaClinicaField,
  Paso2State,
  Paso3State,
  PlanTransicion,
} from '../../../hooks/api/baja';

const HC_FIELDS: { key: HistoriaClinicaField; label: string; placeholder: string; min: number }[] = [
  { key: 'motivoConsulta', label: 'Motivo de consulta inicial', placeholder: '¿Por qué comenzó el proceso terapéutico? ¿Qué trajo el paciente al inicio?', min: 200 },
  { key: 'evaluacionDiagnostica', label: 'Evaluación diagnóstica y enfoque terapéutico', placeholder: 'Impresión clínica, diagnóstico o hipótesis de trabajo, y enfoque o modalidad utilizada durante el proceso.', min: 250 },
  { key: 'antecedentes', label: 'Antecedentes clínicos, red de apoyo y contacto de emergencia', placeholder: 'Antecedentes de salud mental relevantes, tratamientos previos. Red de apoyo del paciente. Contacto de emergencia si se dispone.', min: 150 },
  { key: 'evolucion', label: 'Evolución del proceso', placeholder: 'Describí los momentos clave, cambios significativos y el recorrido del proceso desde el inicio hasta hoy.', min: 400 },
  { key: 'estadoActual', label: 'Estado actual del paciente', placeholder: '¿En qué momento del proceso está hoy? ¿Cómo llega a esta instancia?', min: 200 },
  { key: 'objetivos', label: 'Objetivos terapéuticos cumplidos y pendientes', placeholder: '¿Qué se logró? ¿Qué queda por trabajar o consolidar?', min: 150 },
  { key: 'evaluacionRiesgos', label: 'Evaluación de riesgos y recursos del paciente', placeholder: '¿Qué riesgos existen (ideación suicida, autolesiones, riesgo para terceros u otros)? ¿Qué fortalezas o recursos tiene el paciente?', min: 200 },
  { key: 'observaciones', label: 'Observaciones relevantes del proceso', placeholder: 'Sensibilidades del paciente, dinámicas importantes, aspectos del vínculo relevantes para el seguimiento.', min: 250 },
  { key: 'notaTraspaso', label: 'Nota de traspaso', placeholder: 'Contexto relevante sobre tu participación en este proceso y lo que consideres importante que quede registrado.', min: 150 },
];

const emptyHc = (): HistoriaClinica => ({
  motivoConsulta: '', evaluacionDiagnostica: '', antecedentes: '', evolucion: '', estadoActual: '',
  objetivos: '', evaluacionRiesgos: '', observaciones: '', notaTraspaso: '',
});

function hcFieldsOk(hc: HistoriaClinica): boolean {
  return HC_FIELDS.every((f) => (hc[f.key] ?? '').trim().length >= f.min);
}

function hcFieldsDoneCount(hc: HistoriaClinica): number {
  return HC_FIELDS.filter((f) => (hc[f.key] ?? '').trim().length >= f.min).length;
}

/**
 * Bajas importadas del proyecto Lovable viejo guardan el paso 3 anidado por
 * decisión (`{acompanar, derivar, alta}`, cada rama con su propio
 * `historiaClinica`) en vez del shape plano que usa este componente — y
 * `confirmado`/`validacionIA` viven DENTRO de esa `historiaClinica`, no como
 * hermanos (mismo shape que ya resolvimos del lado del admin en psima,
 * `resolvePlanTransicion` en bajas/page.tsx). Sin esto, `hcFieldsOk` explota
 * apenas una psicóloga con una baja legacy en curso llega al Paso 3, porque
 * `historiaClinica` queda `undefined` en el nivel plano que este componente
 * espera — confirmado contra datos reales (varias bajas en curso, sin
 * completar, con exactamente este shape).
 */
type LegacyHc = Partial<HistoriaClinica> & {
  confirmado?: boolean;
  validacionIA?: HcValidationResult | null;
};
type LegacyBranch = Partial<PlanTransicion> & { historiaClinica?: LegacyHc };
type LegacyPlan = { acompanar?: LegacyBranch; derivar?: LegacyBranch; alta?: LegacyBranch };

function isLegacyPlan(plan: object): plan is LegacyPlan {
  return !('historiaClinica' in plan);
}

function migrateLegacyPlan(decision: 'derivar' | 'acompanar' | 'alta', legacy: LegacyPlan): PlanTransicion {
  const branch = legacy[decision] ?? legacy.acompanar ?? legacy.derivar ?? legacy.alta;
  const legacyHc = branch?.historiaClinica;
  const historiaClinica = HC_FIELDS.reduce((acc, f) => {
    acc[f.key] = legacyHc?.[f.key] ?? '';
    return acc;
  }, {} as HistoriaClinica);

  return {
    decision,
    fechaUltimaSesion: branch?.fechaUltimaSesion ?? null,
    fechaAlta: branch?.fechaAlta ?? null,
    fechaCierre: branch?.fechaCierre ?? null,
    aspectos: branch?.aspectos,
    preferenciaPerfil: branch?.preferenciaPerfil,
    momento: branch?.momento,
    justificacion: branch?.justificacion,
    acuerdo: branch?.acuerdo,
    historiaClinica,
    validacionIA: legacyHc?.validacionIA ?? null,
    confirmado: legacyHc?.confirmado ?? false,
  };
}

function planFor(
  decision: 'derivar' | 'acompanar' | 'alta',
  existing?: PlanTransicion | LegacyPlan,
): PlanTransicion {
  if (!existing) return { decision, historiaClinica: emptyHc() };
  if (isLegacyPlan(existing)) return migrateLegacyPlan(decision, existing);
  return existing;
}

function planCompleto(plan: PlanTransicion): boolean {
  if (!plan.confirmado) return false;
  if (!hcFieldsOk(plan.historiaClinica)) return false;
  if (plan.decision === 'derivar') return !!plan.fechaUltimaSesion;
  if (plan.decision === 'acompanar') return !!plan.fechaAlta;
  return !!plan.justificacion?.trim() && !!plan.acuerdo && !!plan.fechaCierre;
}

function toDateInputValue(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const ACUERDO_OPTIONS = [
  { value: 'conversado', label: 'Sí, fue conversado con el paciente' },
  { value: 'proximas-sesiones', label: 'Aún no, lo haré en las próximas sesiones' },
  { value: 'paciente-propuso', label: 'El paciente lo propuso' },
];

const STATUS_STYLE: Record<string, { color: string; icon: React.ReactNode }> = {
  approved: { color: 'text-emerald-600', icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
  warning: { color: 'text-amber-600', icon: <AlertCircle className="h-3.5 w-3.5" /> },
  rejected: { color: 'text-coral', icon: <AlertCircle className="h-3.5 w-3.5" /> },
};

/** Título + emoji de la sección del plan según la decisión — mismo emoji que
 * `DECISION_OPTIONS` de Paso2Procesos.tsx, para que se lea como continuidad
 * de lo elegido ahí, no como una sección nueva sin relación. */
const PLAN_SECTION: Record<'derivar' | 'acompanar' | 'alta', { emoji: string; title: string }> = {
  derivar: { emoji: '🔄', title: 'Plan de derivación' },
  acompanar: { emoji: '✅', title: 'Plan de acompañamiento' },
  alta: { emoji: '🏁', title: 'Situación a evaluar por el equipo' },
};

interface Props {
  patients: BajaPaciente[];
  paso2: Paso2State | null;
  initial: Paso3State | null;
  onBack: () => void;
  onNext: (state: Paso3State) => void;
  onSave: (state: Paso3State) => void;
}

export const Paso3PlanTransicion: React.FC<Props> = ({ patients, paso2, initial, onBack, onNext, onSave }) => {
  // Solo pacientes con una decisión ya tomada en el paso 2 requieren plan acá.
  const relevantPatients = useMemo(
    () => patients.filter((p) => !!paso2?.planes?.[p.id]?.decision),
    [patients, paso2],
  );

  const [planes, setPlanes] = useState<Paso3State>(() => {
    const base: Paso3State = { ...(initial ?? {}) };
    for (const p of relevantPatients) {
      const decision = paso2?.planes[p.id]?.decision;
      if (!decision) continue;
      base[p.id] = planFor(decision, base[p.id]);
    }
    return base;
  });
  const [activeIdx, setActiveIdx] = useState(0);
  const [validating, setValidating] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const activePatient = relevantPatients[activeIdx];
  const activePlan = activePatient ? planes[activePatient.id] : undefined;

  const updateActivePlan = (patch: Partial<PlanTransicion>) => {
    if (!activePatient) return;
    setPlanes((prev) => {
      const next = { ...prev, [activePatient.id]: { ...prev[activePatient.id], ...patch } };
      onSave(next);
      return next;
    });
  };

  const updateHc = (patch: Partial<HistoriaClinica>) => {
    if (!activePatient || !activePlan) return;
    const nextHc = { ...activePlan.historiaClinica, ...patch };
    updateActivePlan({ historiaClinica: nextHc, validacionIA: null, confirmado: false });
  };

  const handleValidate = async () => {
    if (!activePatient || !activePlan) return;
    setValidating(activePatient.id);
    setValidationError(null);
    try {
      const result: HcValidationResult = await bajaApi.validateHistoriaClinica(activePlan.historiaClinica);
      updateActivePlan({ validacionIA: result, confirmado: activePlan.confirmado && result.overallStatus === 'approved' });
    } catch {
      setValidationError('No pudimos validar la historia clínica. Reintentá en unos segundos.');
    } finally {
      setValidating(null);
    }
  };

  const allComplete = relevantPatients.length === 0 || relevantPatients.every((p) => planCompleto(planes[p.id]));

  if (relevantPatients.length === 0) {
    return (
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Plan de transición</h1>
          <p className="text-sm text-muted-foreground">No declaraste procesos activos en el paso anterior, así que no hay nada que completar acá.</p>
        </header>
        <div className="flex justify-between pt-4 border-t border-border">
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
          <Button type="button" onClick={() => onNext(planes)} className="sm:min-w-[200px]">
            Continuar <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  if (!activePatient || !activePlan) return null;
  const fieldsOk = hcFieldsOk(activePlan.historiaClinica);
  const hcDone = hcFieldsDoneCount(activePlan.historiaClinica);
  const validationApproved = activePlan.validacionIA?.overallStatus === 'approved';
  const planSection = PLAN_SECTION[activePlan.decision];

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8 pb-28">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Plan de transición</h1>
        <p className="text-sm text-muted-foreground">Detallá el plan de transición clínica para cada uno de tus procesos activos.</p>
      </header>

      {/* Mini-stepper de pacientes */}
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-3 shadow-sm">
        <button
          type="button"
          disabled={activeIdx === 0}
          onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
          className="p-1.5 rounded-full border border-border text-gray-500 hover:bg-muted disabled:opacity-30 shrink-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 flex items-center justify-center gap-3">
          <Avatar name={activePatient.nombre} sizeRem={2.5} />
          <div className="text-left">
            <p className="text-base font-bold text-gray-900 leading-tight">{activePatient.nombre}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-xs text-muted-foreground">Paciente {activeIdx + 1} de {relevantPatients.length}</span>
              <span
                className={[
                  'text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full',
                  planCompleto(activePlan) ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700',
                ].join(' ')}
              >
                {planCompleto(activePlan) ? 'Completo' : 'Pendiente'}
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          disabled={activeIdx === relevantPatients.length - 1}
          onClick={() => setActiveIdx((i) => Math.min(relevantPatients.length - 1, i + 1))}
          className="p-1.5 rounded-full border border-border text-gray-500 hover:bg-muted disabled:opacity-30 shrink-0"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Campos específicos por decisión */}
      <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-4">
        <div className="flex items-center gap-2 pb-1">
          <span className="text-lg" aria-hidden>{planSection.emoji}</span>
          <h2 className="text-base font-bold text-gray-900">{planSection.title}</h2>
        </div>

        {activePlan.decision === 'derivar' && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Aspectos a tener en cuenta para la derivación</label>
              <Textarea value={activePlan.aspectos ?? ''} onChange={(e) => updateActivePlan({ aspectos: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Preferencia de perfil profesional para la derivación</label>
              <Textarea value={activePlan.preferenciaPerfil ?? ''} onChange={(e) => updateActivePlan({ preferenciaPerfil: e.target.value })} rows={2} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Fecha estimada de última sesión con vos <span className="text-coral">*</span></label>
              <input
                type="date"
                value={toDateInputValue(activePlan.fechaUltimaSesion)}
                onChange={(e) => updateActivePlan({ fechaUltimaSesion: e.target.value || null })}
                className="rounded-md border border-border bg-white px-3 py-2 text-sm text-gray-900"
              />
            </div>
          </>
        )}
        {activePlan.decision === 'acompanar' && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">¿En qué momento del proceso está el alta natural?</label>
              <Textarea value={activePlan.momento ?? ''} onChange={(e) => updateActivePlan({ momento: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Fecha estimada de alta <span className="text-coral">*</span></label>
              <input
                type="date"
                value={toDateInputValue(activePlan.fechaAlta)}
                onChange={(e) => updateActivePlan({ fechaAlta: e.target.value || null })}
                className="rounded-md border border-border bg-white px-3 py-2 text-sm text-gray-900"
              />
            </div>
          </>
        )}
        {activePlan.decision === 'alta' && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Justificación de la situación <span className="text-coral">*</span></label>
              <Textarea value={activePlan.justificacion ?? ''} onChange={(e) => updateActivePlan({ justificacion: e.target.value })} rows={3} />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-gray-700">¿Fue conversado con el paciente? <span className="text-coral">*</span></legend>
              {ACUERDO_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="radio" name="acuerdo" className="accent-primary" checked={activePlan.acuerdo === opt.value} onChange={() => updateActivePlan({ acuerdo: opt.value })} />
                  {opt.label}
                </label>
              ))}
            </fieldset>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Fecha estimada de cierre <span className="text-coral">*</span></label>
              <input
                type="date"
                value={toDateInputValue(activePlan.fechaCierre)}
                onChange={(e) => updateActivePlan({ fechaCierre: e.target.value || null })}
                className="rounded-md border border-border bg-white px-3 py-2 text-sm text-gray-900"
              />
            </div>
          </>
        )}
      </section>

      {/* Historia clínica */}
      <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
              <FileText className="h-4 w-4" />
            </span>
            <div>
              <h2 className="text-base font-bold text-gray-900">Historia clínica del paciente <span className="text-coral">*</span></h2>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1 max-w-md">
                Es el registro del proceso terapéutico de una persona y tu responsabilidad profesional y contractual
                antes de salir. Será revisada por el equipo antes de cerrar formalmente tu vínculo.
              </p>
            </div>
          </div>
          <span
            className={[
              'text-xs font-semibold px-2.5 py-1 rounded-full shrink-0',
              fieldsOk ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-gray-500',
            ].join(' ')}
          >
            {hcDone} / {HC_FIELDS.length} campos
          </span>
        </div>

        <div className="space-y-5 divide-y divide-border">
          {HC_FIELDS.map((field, i) => {
            const current = activePlan.historiaClinica[field.key] ?? '';
            const len = current.trim().length;
            const ok = len >= field.min;
            const fieldResult = activePlan.validacionIA?.fields.find((f) => f.fieldId === field.key);
            return (
              <div key={field.key} className={['space-y-1.5', i > 0 ? 'pt-5' : ''].join(' ')}>
                <label className="flex items-start gap-2 text-sm font-semibold text-gray-900">
                  <span
                    className={[
                      'flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold shrink-0 mt-0.5',
                      ok ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-300 text-gray-400',
                    ].join(' ')}
                  >
                    {ok ? '✓' : i + 1}
                  </span>
                  <span>{field.label} <span className="text-coral">*</span></span>
                </label>
                <Textarea
                  value={current}
                  onChange={(e) => updateHc({ [field.key]: e.target.value } as Partial<HistoriaClinica>)}
                  placeholder={field.placeholder}
                  rows={4}
                  className="ml-7 w-[calc(100%-1.75rem)]"
                />
                <p className={['ml-7 text-xs', ok ? 'text-emerald-600 font-medium' : 'text-gray-400'].join(' ')}>
                  {len} / {field.min} caracteres mínimos
                </p>
                {fieldResult && (
                  <p className={['ml-7 text-xs flex items-center gap-1', STATUS_STYLE[fieldResult.status]?.color].join(' ')}>
                    {STATUS_STYLE[fieldResult.status]?.icon} {fieldResult.message}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Validación IA + confirmación */}
      <section className="rounded-2xl border border-primary/25 bg-primary/5 p-5 sm:p-6 space-y-3">
        <button
          type="button"
          onClick={handleValidate}
          disabled={!fieldsOk || validating === activePatient.id}
          className={[
            'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors',
            !fieldsOk || validating === activePatient.id
              ? 'border-border bg-muted text-gray-400 cursor-not-allowed'
              : 'border-primary bg-primary text-white hover:bg-primary/90',
          ].join(' ')}
        >
          {validating === activePatient.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {validating === activePatient.id ? 'Validando…' : 'Validar historia clínica'}
        </button>
        <p className="text-xs text-muted-foreground">
          {fieldsOk ? 'Verificá que la historia clínica incluya todo lo necesario antes de confirmar.' : 'Completá todos los campos con su longitud mínima para habilitar la validación.'}
        </p>
        {validationError && <p className="text-xs text-coral">{validationError}</p>}

        {activePlan.validacionIA && (
          <div
            className={[
              'rounded-xl border px-4 py-3 text-sm',
              validationApproved ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800',
            ].join(' ')}
          >
            <p className="font-semibold">
              {validationApproved ? '✓ Historia clínica aprobada' : activePlan.validacionIA.overallStatus === 'rejected' ? 'Hay campos que necesitan revisión' : 'Hay campos que se pueden mejorar'}
            </p>
            <p className="text-xs mt-1 opacity-80">{activePlan.validacionIA.disclaimer}</p>
          </div>
        )}

        <label className={['flex items-start gap-2 text-sm pt-1', validationApproved ? 'text-gray-900' : 'text-gray-400'].join(' ')}>
          <input
            type="checkbox"
            className="accent-primary mt-0.5"
            disabled={!validationApproved}
            checked={!!activePlan.confirmado}
            onChange={(e) => updateActivePlan({ confirmado: e.target.checked })}
          />
          Confirmo que la historia clínica de este paciente está completa y es fiel al proceso realizado.
        </label>
      </section>

      <div className="flex justify-between pt-4 border-t border-border">
        <Button type="button" variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>
        <Button type="button" disabled={!allComplete} onClick={() => onNext(planes)} className="sm:min-w-[200px]">
          Continuar <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default Paso3PlanTransicion;
